import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBooking } from "../context/BookingContext";
import {
  Card,
  CardContent,
  Typography,
  Button,
  MenuItem,
  Select,
  Container,
  Grid,
  Box,
  Paper,
  Alert,
  Skeleton,
  Divider,
  InputLabel,
  FormControl,
  FormHelperText,
  IconButton,
  Snackbar,
  Stack,
  Avatar,
  Tooltip,
  LinearProgress,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { getListingByID } from "../apis/LaneCageApi";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InfoIcon from "@mui/icons-material/Info";
import SportsCricketIcon from "@mui/icons-material/SportsCricket";
import {
  applyFacilityBookingPricing,
  getBookingOfferHeadline,
  getEventAvailability,
  getOfferCalendarDates,
  getOffersCoveringDate,
  getPublicBookingOffers,
  todayYmd,
} from "../utils/facilityEvents";
import {
  getBookingBlackoutBody,
  getBookingBlackoutDateObjects,
  getBookingBlackoutTitle,
  HALLOWEEN_WALK_IN_YMD,
  isBookingBlackoutDate,
  isHalloweenWalkInDay,
} from "../utils/bookingBlackoutDates";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AvailableSlots from "../components/AvailableSlots";
import theme from "../components/theme/Theme";
import { getAvailableSlots } from "../apis/bookingApi";
import { extractTimeFromDateString } from "../hooks/Date";

const BookingCalendar = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    selectedListing,
    setSelectedListing,
    selectedDate,
    setSelectedDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    checkAvailability,
    createTempBooking,
    setBookingId,
    setTimeRemaining,
  } = useBooking();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [timeError, setTimeError] = useState("");
  const [dateError, setDateError] = useState("");
  const [activeStep, setActiveStep] = useState(0);

  const passSelectedDate = (date: string) => {
    const newDate = new Date(date);
    const year = newDate.getFullYear();
    const month = newDate.getMonth() + 1;
    const day = newDate.getDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      getListingByID(id)
        .then((data) => {
          setSelectedListing(data.laneOrCage);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching listing:", err);
          setError("Failed to load listing details. Please try again.");
          setLoading(false);
        });
    }
  }, [id, setSelectedListing]);

  useEffect(() => {
    // Reset time error when times change
    if (startTime && endTime) {
      const [startHour, startMinute] = startTime.split(":").map(Number);
      const [endHour, endMinute] = endTime.split(":").map(Number);

      const startTotalMinutes = startHour * 60 + startMinute;
      const endTotalMinutes = endHour * 60 + endMinute;

      if (endTotalMinutes <= startTotalMinutes) {
        setTimeError("End time must be after start time");
      } else {
        setTimeError("");
      }
    }
  }, [startTime, endTime]);

  useEffect(() => {
    // Reset date error when date changes
    if (selectedDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const selected = new Date(selectedDate);
      selected.setHours(0, 0, 0, 0);

      if (selected < today) {
        setDateError("Cannot book dates in the past");
      } else if (isBookingBlackoutDate(selectedDate)) {
        setDateError("");
      } else {
        setDateError("");
        setActiveStep(1); // Move to time selection when date is selected
      }
    } else {
      setDateError("");
    }
  }, [selectedDate]);

  useEffect(() => {
    // Move to summary when both times are selected and valid
    if (startTime && endTime && !timeError) {
      setActiveStep(2);
    }
  }, [startTime, endTime, timeError]);

  const handleCheckAvailability = async () => {
    // Validate inputs first
    if (!selectedDate) {
      setDateError("Please select a date");
      return;
    }

    if (!startTime || !endTime) {
      setTimeError("Please select both start and end times");
      return;
    }

    if (timeError || dateError) {
      return;
    }

    // Check for time slot overlap before proceeding
    try {
      if (!selectedDate || !id) {
        throw new Error("Selected date or listing ID is missing.");
      }
      const response = await getAvailableSlots(selectedDate ? passSelectedDate(selectedDate) : "", id);
      const bookedSlots = response.data.bookedSlots || [];

      // Convert time to minutes for comparison
      const timeToMinutes = (time: string): number => {
        const [hours, minutes] = time.split(":").map(Number);
        return hours * 60 + minutes;
      };

      // Check if selected time overlaps with any booked slots
      const hasOverlap = bookedSlots.some((slot: any) => {
        const selectedStart = timeToMinutes(startTime);
        const selectedEnd = timeToMinutes(endTime);
        const slotStart = timeToMinutes(
          extractTimeFromDateString(slot.startTime)
        );
        const slotEnd = timeToMinutes(extractTimeFromDateString(slot.endTime));

        return (
          (selectedStart >= slotStart && selectedStart < slotEnd) ||
          (selectedEnd > slotStart && selectedEnd <= slotEnd) ||
          (selectedStart <= slotStart && selectedEnd >= slotEnd)
        );
      });

      if (hasOverlap) {
        setError(
          "Sorry, the selected time slot is already booked. Please choose a different time."
        );
        setShowError(true);
        return;
      }

      // If no overlap, proceed with availability check
      const available = await checkAvailability();
      const responsetemp = await createTempBooking();
      setBookingId(responsetemp.bookingId);
      setTimeRemaining(responsetemp.expiresAt);
      if (available) {
        navigate(`/confirm-booking/${id}`);
      } else {
        setError(
          "Sorry, the selected time slot is not available. Please try a different time."
        );
        setShowError(true);
      }
    } catch (err) {
      setError("Error checking availability. Please try again.");
      setShowError(true);
    }
  };

  const calculateDuration = () => {
    if (!startTime || !endTime) return 0;

    // Parse hours and minutes separately
    const [startHourStr, startMinStr] = startTime.split(":");
    const [endHourStr, endMinStr] = endTime.split(":");

    const startHour = parseInt(startHourStr);
    const startMin = parseInt(startMinStr);
    const endHour = parseInt(endHourStr);
    const endMin = parseInt(endMinStr);

    const startTotalMinutes = startHour * 60 + startMin;
    const endTotalMinutes = endHour * 60 + endMin;

    const durationMinutes =
      endTotalMinutes > startTotalMinutes
        ? endTotalMinutes - startTotalMinutes
        : 0;
    return durationMinutes / 60;
  };

  const getBaseCost = () => {
    if (!selectedListing || !startTime || !endTime) return 0;

    const duration = calculateDuration();
    const hourlyRate = selectedListing.pricePerHour || 0;
    const fullHours = Math.floor(duration);
    const remainingMinutes = Math.round((duration - fullHours) * 60);

    let cost = 0;
    if (duration === 0.5) {
      cost = hourlyRate * 0.5;
    } else {
      cost = fullHours * hourlyRate;
      if (remainingMinutes === 30) {
        cost += hourlyRate * 0.5;
      }
    }

    return Math.round(cost * 100) / 100;
  };

  const getPricingBreakdown = () =>
    applyFacilityBookingPricing(getBaseCost(), selectedDate);

  const calculateCost = () => getPricingBreakdown().total;

  // Disable past dates and holiday blackouts. Walk-in days stay selectable so the message can show.
  const holidayDates = getBookingBlackoutDateObjects().filter(
    (date) => !isHalloweenWalkInDay(date)
  );

  // React-Day-Picker disabled config
  const disabledDays = { before: new Date(), dates: holidayDates };

  const isHolidaySelected = isBookingBlackoutDate(selectedDate);
  const publicBookingOffers = getPublicBookingOffers();
  const offerCalendarDates = getOfferCalendarDates().filter(
    (date) => !isBookingBlackoutDate(date)
  );
  const walkInCalendarDates = [HALLOWEEN_WALK_IN_YMD].map((ymd) => {
    const [year, month, day] = ymd.split("-").map(Number);
    return new Date(year, month - 1, day);
  });
  const showWalkInNote = todayYmd() <= HALLOWEEN_WALK_IN_YMD;
  const selectedDateOffers = isBookingBlackoutDate(selectedDate)
    ? []
    : getOffersCoveringDate(selectedDate);

  // Custom css for day picker to match the theme - more compact
  const dayPickerStyles = `
    .rdp {
      margin: 0;
      width: 100%;
    }
    .rdp-months {
      justify-content: center;
    }
    .rdp-month {
      width: 100%;
    }
    .rdp-table {
      width: 100%;
      max-width: 100%;
    }
    .rdp-cell {
      padding: 0;
    }
    .rdp-day {
      margin: 0;
      width: 36px;
      height: 36px;
      border-radius: 50%;
    }
    .rdp-day_selected:not([disabled]) { 
      background-color: ${theme.palette.primary.main};
      color: white;
      font-weight: bold;
    }
    .rdp-day_selected:hover:not([disabled]) { 
      background-color: ${theme.palette.primary.dark};
      color: white;
    }
    .rdp-day:hover:not([disabled]) {
      background-color: ${theme.palette.primary.light}20;
      color: ${theme.palette.primary.dark};
    }
    .rdp-head_cell {
      font-weight: bold;
      color: ${theme.palette.primary.main};
      padding: 0.5rem 0;
    }
    .rdp-caption {
      margin-bottom: 0.5rem;
    }
    .rdp-caption_label {
      font-weight: bold;
      font-size: 1rem;
    }
    .rdp-button:focus:not([disabled]) {
      background-color: ${theme.palette.primary.light}30;
      color: ${theme.palette.primary.dark};
    }
    .rdp-nav_button:hover:not([disabled]) {
      background-color: ${theme.palette.primary.light}20;
    }
    .rdp-offer:not(.rdp-selected) .rdp-day_button,
    .rdp-day_offer:not(.rdp-day_selected) {
      background-color: #e8f5e9;
      color: #1b5e20;
      font-weight: 700;
      border: 2px solid #43a047;
    }
    .rdp-offer.rdp-selected .rdp-day_button,
    .rdp-day_offer.rdp-day_selected {
      box-shadow: inset 0 0 0 2px #43a047;
    }
    .rdp-walkin:not(.rdp-selected) .rdp-day_button,
    .rdp-day_walkin:not(.rdp-day_selected) {
      background-color: #fff3e0;
      color: #e65100;
      font-weight: 700;
      border: 2px solid #ef6c00;
    }
    .rdp-walkin.rdp-selected .rdp-day_button,
    .rdp-day_walkin.rdp-day_selected {
      box-shadow: inset 0 0 0 2px #ef6c00;
    }
  `;
  // Time configuration for different days
  const timeConfig = {
    weekday: {
      startHour: 13,
      endHour: 22,
      interval: 30,
    },
    friday: {
      startHour: 13,
      endHour: 22,
      interval: 30,
    },
    saturday: {
      startHour: 10,
      endHour: 22,
      interval: 30,
    },
    sunday: {
      startHour: 10,
      endHour: 22,
      interval: 30,
    },
    // weekend: {
    //   startHour: 10,
    //   endHour: 21,
    //   interval: 30,
    // },
  };

  // Check if a date is a weekend
  // const isWeekend = (date: Date): boolean => {
  //   const day = date.getDay();
  //   return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
  // };

  const isFriday = (date: Date): boolean => {
    const day = date.getDay();
    return day === 5; // 0 = Sunday, 6 = Saturday
  };

  const isSaturday = (date: Date): boolean => {
    const day = date.getDay();
    return day === 6; // 0 = Sunday, 6 = Saturday
  };
  const isSunday = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0; // 0 = Sunday, 6 = Saturday
  };

  const generateStartTimeOptions = () => {
    const date = selectedDate ? new Date(selectedDate) : new Date();
    const today = new Date();
    const config = isSaturday(date)
      ? timeConfig.saturday
      : isSunday(date)
      ? timeConfig.sunday
      : isFriday(date)
      ? timeConfig.friday
      : timeConfig.weekday;
    // isWeekend(date)
    //   ? timeConfig.weekend
    //   : isFriday(date)
    //   ? timeConfig.friday
    //   : timeConfig.weekday;

    // Current time for comparison
    const currentHour = today.getHours();
    const currentMinute = today.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;

    // Calculate number of time slots
    const totalSlots =
      ((config.endHour - config.startHour) * 60) / config.interval - 1; // End 30 mins early

    return Array.from({ length: totalSlots + 1 }, (_, i) => {
      const totalMinutes = config.startHour * 60 + i * config.interval;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      // Skip times before current time if date is today
      if (
        selectedDate &&
        new Date(selectedDate).toDateString() === today.toDateString() &&
        totalMinutes < currentTimeInMinutes
      ) {
        return null;
      }

      const hour = hours < 10 ? `0${hours}` : `${hours}`;
      const minute = minutes < 10 ? `0${minutes}` : `${minutes}`;
      const ampm = hours < 12 ? "AM" : "PM";
      const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      const hour12Formatted = hour12 < 10 ? `0${hour12}` : `${hour12}`;

      return (
        <MenuItem key={`${hour}:${minute}`} value={`${hours}:${minute}`}>
          {`${hour}:${minute}`} ({`${hour12Formatted}:${minute} ${ampm}`})
        </MenuItem>
      );
    }).filter(Boolean); // Remove null values
  };

  // Generate time options for endTime that start 30 mins after the selected startTime
  const generateEndTimeOptions = () => {
    if (!startTime) {
      return <MenuItem disabled>Select a start time first</MenuItem>;
    }

    const date = selectedDate ? new Date(selectedDate) : new Date();
    const today = new Date();
    const config = isSaturday(date)
      ? timeConfig.saturday
      : isSunday(date)
      ? timeConfig.sunday
      : isFriday(date)
      ? timeConfig.friday
      : timeConfig.weekday;

    // isWeekend(date)
    //   ? timeConfig.weekend
    //   : isFriday(date)
    //   ? timeConfig.friday
    //   : timeConfig.weekday;

    // Parse the selected start time
    const [startHour, startMinute] = startTime.split(":").map(Number);

    // Add 30 minutes to the start time
    let minEndTimeMinutes = startHour * 60 + startMinute + 30;

    // Calculate number of time slots from the minimum end time to the end hour
    const totalSlots =
      Math.floor((config.endHour * 60 - minEndTimeMinutes) / config.interval) +
      1;

    if (totalSlots <= 0) {
      return <MenuItem disabled>No available end times</MenuItem>;
    }

    // Current time for comparison
    const currentHour = today.getHours();
    const currentMinute = today.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;

    return Array.from({ length: totalSlots }, (_, i) => {
      const totalMinutes = minEndTimeMinutes + i * config.interval;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      // Skip times before current time if date is today
      if (
        selectedDate &&
        new Date(selectedDate).toDateString() === today.toDateString() &&
        totalMinutes < currentTimeInMinutes
      ) {
        return null;
      }

      const hour = hours < 10 ? `0${hours}` : `${hours}`;
      const minute = minutes < 10 ? `0${minutes}` : `${minutes}`;
      const ampm = hours < 12 ? "AM" : "PM";
      const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      const hour12Formatted = hour12 < 10 ? `0${hour12}` : `${hour12}`;

      return (
        <MenuItem key={`${hour}:${minute}`} value={`${hours}:${minute}`}>
          {`${hour}:${minute}`} ({`${hour12Formatted}:${minute} ${ampm}`})
        </MenuItem>
      );
    }).filter(Boolean); // Remove null values
  };

  // Format time for display
  const formatTimeDisplay = (time: any) => {
    if (!time) return "";
    const [hour, minutes] = time.split(":");
    const hourInt = parseInt(hour);
    const ampm = hourInt < 12 ? "AM" : "PM";
    const hour12 = hourInt === 0 ? 12 : hourInt > 12 ? hourInt - 12 : hourInt;
    const hour12Formatted = hour12 < 10 ? `0${hour12}` : `${hour12}`;
    return `${hour12Formatted}:${minutes} ${ampm}`;
  };

  const steps = ["Select Date", "Select Time", "Booking Summary"];

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", mb: isMobile ? 2 : 0 }}
        >
          <IconButton
            onClick={() => {
              setSelectedDate("");
              setStartTime("");
              setEndTime("");
              navigate('/listings');
            }}
            sx={{ mr: 1 }}
            aria-label="Go back"
            color="primary"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            Book Your Slot
          </Typography>
        </Box>

        {!loading && selectedListing && (
          <Stepper
            activeStep={activeStep}
            sx={{ width: isMobile ? "100%" : "auto" }}
            alternativeLabel={!isMobile}
            orientation={isMobile ? "horizontal" : "horizontal"}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{isMobile ? "" : label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Box>

      {loading ? (
        <Card sx={{ p: 2, boxShadow: 2, borderRadius: 1, mb: 3 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Loading booking information...
            </Typography>
            <LinearProgress color="primary" sx={{ mb: 2 }} />
            <Skeleton
              variant="rectangular"
              height={40}
              width="60%"
              sx={{ mb: 1 }}
            />
            <Skeleton
              variant="rectangular"
              height={20}
              width="100%"
              sx={{ mb: 0.5 }}
            />
            <Skeleton
              variant="rectangular"
              height={20}
              width="80%"
              sx={{ mb: 2 }}
            />
            <Skeleton
              variant="rectangular"
              height={250}
              width="100%"
              sx={{ mb: 2 }}
            />
            <Skeleton
              variant="rectangular"
              height={50}
              width="100%"
              sx={{ mb: 1 }}
            />
            <Skeleton variant="rectangular" height={50} width="100%" />
          </CardContent>
        </Card>
      ) : error && !selectedListing ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Button
            sx={{ mt: 1 }}
            variant="outlined"
            size="small"
            onClick={() => navigate("/")}
          >
            Back to Listings
          </Button>
        </Alert>
      ) : selectedListing ? (
        <Grid container spacing={2}>
          {/* Listing Details - More compact */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                boxShadow: 2,
                borderRadius: 1,
                height: "fit-content",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: 120,
                  width: "100%",
                  bgcolor: "primary.main",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                  p: 1.5,
                  position: "relative",
                }}
              >
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: "white",
                    color: "primary.main",
                    boxShadow: 1,
                    mb: 0.5,
                  }}
                >
                  <SportsCricketIcon sx={{ fontSize: 35 }} />
                </Avatar>
                <Typography variant="h6" fontWeight="bold" align="center">
                  {selectedListing.name}
                </Typography>
              </Box>

              <CardContent sx={{ py: 2 }}>
                {selectedListing.location && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      mb: 1.5,
                    }}
                  >
                    <LocationOnIcon
                      sx={{
                        color: "primary.main",
                        mr: 1,
                        fontSize: 20,
                        mt: 0.3,
                      }}
                    />
                    <Typography variant="body2" color="text.primary">
                      {selectedListing.location}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
                  {selectedListing.description ||
                    "Experience the best sports facility in town."}
                </Typography>

                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AttachMoneyIcon sx={{ color: "success.main", mr: 0.5 }} />
                  <Typography
                    variant="h6"
                    color="success.main"
                    fontWeight="bold"
                  >
                    {selectedListing.pricePerHour}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 0.5, mt: 0.3 }}
                  >
                    per hour
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Booking Form - More compact */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                boxShadow: 2,
                borderRadius: 1,
                overflow: "hidden",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <CalendarMonthIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1" fontWeight="medium">
                  Select Your Booking Details
                </Typography>
              </Box>
              <CardContent sx={{ p: 2 }}>
                {/* Date and Time Selection - Side by Side */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {/* Calendar Selection */}
                  <Grid item xs={12} md={7}>
                    <Box sx={{ mb: { xs: 2, md: 0 } }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <EventIcon
                          sx={{ color: "primary.main", mr: 1, fontSize: 20 }}
                        />
                        <Typography variant="subtitle1" fontWeight="medium">
                          Select Date
                        </Typography>
                      </Box>

                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          border: 1,
                          borderColor: dateError ? "error.main" : "divider",
                          borderRadius: 1,
                          bgcolor: "background.paper",
                          position: "relative",
                          width: "fit-content",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: "primary.main",
                          },
                        }}
                      >
                        <style>{dayPickerStyles}</style>
                        <DayPicker
                          mode="single"
                          selected={
                            selectedDate ? new Date(selectedDate) : undefined
                          }
                          onSelect={(date) => {
                            if (date) {
                              setSelectedDate(date.toString());
                            }
                          }}
                          disabled={disabledDays}
                          modifiers={{
                            offer: offerCalendarDates,
                            walkIn: walkInCalendarDates,
                          }}
                          modifiersClassNames={{
                            offer: "rdp-offer",
                            walkIn: "rdp-walkin",
                          }}
                          showOutsideDays
                          required
                          styles={{
                            caption: { color: theme.palette.primary.main },
                          }}
                        />
                        {(publicBookingOffers.length > 0 || showWalkInNote) && (
                          <Box sx={{ mt: 1.5, px: 0.5 }}>
                            {publicBookingOffers.map((event) => (
                              <Typography
                                key={event.id}
                                variant="caption"
                                color="success.dark"
                                sx={{ display: "block", lineHeight: 1.4 }}
                              >
                                Highlighted dates:{" "}
                                {getBookingOfferHeadline(event)}
                              </Typography>
                            ))}
                            {showWalkInNote && (
                              <Typography
                                variant="caption"
                                sx={{ display: "block", lineHeight: 1.4, color: "#e65100" }}
                              >
                                October 31: walk-ins are free. Online bookings are closed.
                              </Typography>
                            )}
                          </Box>
                        )}
                        {selectedDateOffers.length > 0 &&
                          selectedDateOffers.some(
                            (event) =>
                              !event.live &&
                              getEventAvailability(event) === "from_start"
                          ) && (
                            <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                              {selectedDateOffers
                                .filter(
                                  (event) =>
                                    !event.live &&
                                    getEventAvailability(event) === "from_start"
                                )
                                .map((event) => (
                                  <Typography key={event.id} variant="body2">
                                    Book on {event.name} itself to get this
                                    offer. Advance bookings are charged
                                    normally.
                                  </Typography>
                                ))}
                            </Alert>
                          )}
                        {dateError && (
                          <FormHelperText error>{dateError}</FormHelperText>
                        )}
                      </Paper>
                    </Box>
                  </Grid>

                  {/* Time Selection / Holiday Message */}
                  <Grid item xs={12} md={5}>
                    {isHolidaySelected ? (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          p: 3,
                        }}
                      >
                        <Typography
                          variant="h6"
                          color="error"
                          fontWeight="bold"
                          gutterBottom
                        >
                          {getBookingBlackoutTitle(selectedDate)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getBookingBlackoutBody(selectedDate)}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Box>
                          <Box
                            sx={{ display: "flex", alignItems: "center", mb: 1 }}
                          >
                            <AccessTimeIcon
                              sx={{ color: "primary.main", mr: 1, fontSize: 20 }}
                            />
                            <Typography variant="subtitle1" fontWeight="medium">
                              Select Time
                            </Typography>
                          </Box>

                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              border: 1,
                              borderColor: timeError ? "error.main" : "divider",
                              borderRadius: 1,
                              bgcolor: "background.paper",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                            }}
                          >
                            <FormControl
                              fullWidth
                              error={!!timeError}
                              size="small"
                              sx={{ mb: 3 }}
                            >
                              <InputLabel id="start-time-label">
                                Start Time
                              </InputLabel>
                              <Select
                                labelId="start-time-label"
                                label="Start Time"
                                value={startTime}
                                onChange={(e) => {
                                  setStartTime(e.target.value);
                                  // Reset end time when start time changes
                                  setEndTime("");
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: timeError
                                      ? "error.main"
                                      : "primary.light",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "primary.main",
                                  },
                                }}
                              >
                                {generateStartTimeOptions()}
                              </Select>
                            </FormControl>

                            <FormControl fullWidth error={!!timeError} size="small">
                              <InputLabel id="end-time-label">End Time</InputLabel>
                              <Select
                                labelId="end-time-label"
                                label="End Time"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                sx={{
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: timeError
                                      ? "error.main"
                                      : "primary.light",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "primary.main",
                                  },
                                }}
                              >
                                {generateEndTimeOptions()}
                              </Select>
                              {timeError && (
                                <FormHelperText error>{timeError}</FormHelperText>
                              )}
                            </FormControl>
                          </Paper>
                        </Box>

                        {/* <Box>Available Slots</Box> */}
                        <AvailableSlots
                          selectedDate={selectedDate ? passSelectedDate(selectedDate) : ""}
                          startTime={startTime}
                          endTime={endTime}
                          listingId={id}
                        />
                      </>
                    )}
                  </Grid>
                </Grid>

                {/* Booking Summary - Below Date and Time */}
                {selectedDate &&
                  !isHolidaySelected &&
                  startTime &&
                  endTime &&
                  !timeError &&
                  !dateError && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "#f8f9ff", // Softer background color
                        color: "text.primary",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1.5,
                          pb: 1.5,
                          borderBottom: 1,
                          borderColor: "divider",
                        }}
                      >
                        <ConfirmationNumberIcon
                          sx={{ color: "primary.main", mr: 1 }}
                        />
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          color="primary.main"
                        >
                          Booking Summary
                        </Typography>
                      </Box>

                      <Stack spacing={1.5}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                          }}
                        >
                          <EventIcon
                            sx={{ color: "primary.main", mr: 1, mt: 0.3 }}
                            fontSize="small"
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              fontWeight="bold"
                            >
                              Date
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {new Date(selectedDate).toLocaleDateString(
                                "en-US",
                                {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                }
                              )}
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                          }}
                        >
                          <AccessTimeIcon
                            sx={{ color: "primary.main", mr: 1, mt: 0.3 }}
                            fontSize="small"
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              fontWeight="bold"
                            >
                              Time
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {formatTimeDisplay(startTime)} to{" "}
                              {formatTimeDisplay(endTime)} (
                              {calculateDuration()} hours)
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                          }}
                        >
                          <LocationOnIcon
                            sx={{ color: "primary.main", mr: 1, mt: 0.3 }}
                            fontSize="small"
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              fontWeight="bold"
                            >
                              Location
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {selectedListing.location ||
                                "The Bat Cage, Unit 1, 50A Neptune Crescent"}
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            mt: 1,
                            p: 1.5,
                            bgcolor: "#f0f7ff",
                            borderRadius: 1,
                            border: 1,
                            borderColor: "primary.light",
                          }}
                        >
                          {(() => {
                            const pricing = getPricingBreakdown();
                            const showBreakdown =
                              pricing.promoActive || pricing.taxWaived;
                            return (
                              <>
                                {pricing.notices.map((event) => (
                                  <Alert
                                    key={event.id}
                                    severity="success"
                                    sx={{ mb: 1.5, py: 0.5 }}
                                  >
                                    {event.bookingLabel}
                                    {pricing.promoActive &&
                                    (event.bookingPercentOff ?? 0) > 0
                                      ? ` — you save $${pricing.discountAmount.toFixed(2)}`
                                      : ""}
                                  </Alert>
                                ))}
                                {pricing.promoActive && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      mb: 0.5,
                                    }}
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                      Subtotal
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      ${pricing.subtotal.toFixed(2)}
                                    </Typography>
                                  </Box>
                                )}
                                {pricing.promoActive && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      mb: 0.5,
                                    }}
                                  >
                                    <Typography variant="body2" color="success.main">
                                      Event discount ({pricing.discountPercent}% off)
                                    </Typography>
                                    <Typography variant="body2" color="success.main">
                                      −${pricing.discountAmount.toFixed(2)}
                                    </Typography>
                                  </Box>
                                )}
                                {pricing.taxWaived && (
                                  <Typography
                                    variant="caption"
                                    color="success.main"
                                    sx={{ display: "block", mb: 0.5 }}
                                  >
                                    HST waived at checkout
                                  </Typography>
                                )}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    color="text.primary"
                                    fontWeight="bold"
                                  >
                                    {showBreakdown ? "You pay" : "Total Cost"}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <AttachMoneyIcon
                                      sx={{ color: "success.main", mr: 0.5 }}
                                    />
                                    <Typography
                                      variant="h6"
                                      color="success.main"
                                      fontWeight="bold"
                                    >
                                      {pricing.total.toFixed(2)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </>
                            );
                          })()}
                        </Box>
                      </Stack>
                    </Paper>
                  )}

                {/* Booking Button - At the bottom */}
                <Tooltip
                  title={
                    !selectedDate ||
                    isHolidaySelected ||
                    !startTime ||
                    !endTime ||
                    !!timeError ||
                    !!dateError
                      ? "Please complete all required fields to continue"
                      : "Continue to booking confirmation"
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      fullWidth
                      size="medium"
                      sx={{
                        py: 1.25,
                        fontWeight: "bold",
                        textTransform: "none",
                        borderRadius: 1,
                        boxShadow: 2,
                        "&:hover": {
                          boxShadow: 3,
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                      onClick={handleCheckAvailability}
                      disabled={
                        !selectedDate ||
                        isHolidaySelected ||
                        !startTime ||
                        !endTime ||
                        !!timeError ||
                        !!dateError
                      }
                    >
                      Check Availability & Continue
                    </Button>
                  </span>
                </Tooltip>
              </CardContent>
            </Card>

            {/* Additional Information Card - More compact */}
            <Card
              sx={{
                p: 1.5,
                boxShadow: 1,
                borderRadius: 1,
                bgcolor: "info.50", // Lighter blue
                borderLeft: 3,
                borderColor: "info.main",
              }}
            >
              <CardContent
                sx={{ display: "flex", alignItems: "flex-start", p: 0.5 }}
              >
                <InfoIcon
                  sx={{ color: "info.main", mr: 1, fontSize: 20, mt: 0.3 }}
                />
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    color="info.dark"
                    gutterBottom
                  >
                    Booking Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your booking is confirmed only after checking availability.
                    You'll be able to review all details before finalizing your
                    payment. Cancellations made 24 hours before your scheduled
                    time are eligible for a full refund.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Typography textAlign="center">No listing found.</Typography>
      )}

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BookingCalendar;
